import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import spacesService from "../services/spacesService";
import styles from "./RequestViewingModal.module.css";

const RequestViewingModal = ({ isOpen, onClose, propertyId, propertyName, onViewingBooked }) => {
  const [loading, setLoading] = useState(false);
  const [viewings, setViewings] = useState([]);
  const [confirmations, setConfirmations] = useState([]);
  const [error, setError] = useState(null);
  const [alternativeViewings, setAlternativeViewings] = useState([]);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    if (isOpen && propertyId) {
      loadUpcomingViewings();
    }
  }, [isOpen, propertyId]);

  const loadUpcomingViewings = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log(`🔍 [MODAL] Loading upcoming viewings for property ${propertyId}`);

      const response = await spacesService.getUpcomingViewingsForProperty(propertyId);

      console.log(`✅ [MODAL] Loaded viewings data:`, response);

      setViewings(response.viewings || []);
      setConfirmations(response.confirmations || []);

      if (response.viewings?.length === 0) {
        setError("No upcoming viewings are currently available for this property.");
      }
    } catch (err) {
      console.error("❌ [MODAL] Error loading viewings:", err);
      // Show specific error message from API if available
      const errorMessage = err.response?.data?.message || err.message || "Failed to load upcoming viewings. Please try again.";
      setError(errorMessage);
      setViewings([]);
      setConfirmations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAttendance = async (viewingId) => {
    try {
      setActionLoading(prev => ({ ...prev, [viewingId]: true }));
      
      console.log(`🔍 [MODAL] Confirming attendance for viewing ${viewingId}`);
      
      const result = await spacesService.confirmViewingAttendance(viewingId);
      
      console.log(`✅ [MODAL] Attendance confirmed:`, result);
      
      // Update local state
      setConfirmations(prev => [...prev, viewingId]);
      
      toast.success("Your attendance has been confirmed!", {
        position: "top-right",
        autoClose: 3000,
      });
      
      // Reload viewings to get updated data
      await loadUpcomingViewings();
      
      // ✅ Notify parent component to refresh spaces list (so "Request Viewing" button updates)
      if (onViewingBooked) {
        console.log("🔄 [MODAL] Notifying parent component to refresh spaces list");
        onViewingBooked();
      }
      
            } catch (err) {
          console.error("❌ [MODAL] Error confirming attendance:", err);
          
          // ✅ Laravel Enhanced: Handle capacity exceeded with alternative viewings
          if (err.response?.data?.error) {
            const errorMessage = err.response.data.error;
            
            // Check if this is a capacity error with alternatives
            if (err.response.data.code === 'VIEWING_FULLY_BOOKED' && err.response.data.alternativeViewings) {
              console.log("📅 [MODAL] Viewing fully booked, showing alternatives:", err.response.data.alternativeViewings);
              
              setAlternativeViewings(err.response.data.alternativeViewings);
              setError(`${errorMessage}\n\nAlternative viewings are available below:`);
              
              toast.error(errorMessage, {
                position: "top-right",
                autoClose: 8000, // Longer timeout for capacity errors
              });
            } else {
              setError(errorMessage);
              toast.error(errorMessage, {
                position: "top-right",
                autoClose: 5000,
              });
            }
          } else {
            const fallbackMessage = "Failed to confirm attendance. Please try again.";
            setError(fallbackMessage);
            toast.error(fallbackMessage, {
              position: "top-right",
              autoClose: 5000,
            });
          }
        } finally {
      setActionLoading(prev => ({ ...prev, [viewingId]: false }));
    }
  };

  const handleCancelAttendance = async (viewingId) => {
    try {
      setActionLoading(prev => ({ ...prev, [viewingId]: true }));
      
      console.log(`🔍 [MODAL] Cancelling attendance for viewing ${viewingId}`);
      
      const result = await spacesService.cancelViewingAttendance(viewingId);
      
      console.log(`✅ [MODAL] Attendance cancelled:`, result);
      
      // Update local state
      setConfirmations(prev => prev.filter(id => id !== viewingId));
      
      toast.success("Your attendance has been cancelled.", {
        position: "top-right",
        autoClose: 3000,
      });
      
      // Reload viewings to get updated data
      await loadUpcomingViewings();
      
    } catch (err) {
      console.error("❌ [MODAL] Error cancelling attendance:", err);
      toast.error("Failed to cancel attendance. Please try again.", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setActionLoading(prev => ({ ...prev, [viewingId]: false }));
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    // Handle HH:MM or HH:MM:SS format
    return timeString.includes(":") ? timeString.substring(0, 5) : timeString;
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        {/* Modal Header */}
        <div className={styles.modalHeader}>
          <h4 className={styles.modalTitle}>Request Viewing</h4>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        {/* Modal Body */}
        <div className={styles.modalBody}>
          {propertyName && (
            <div className={styles.propertyInfo}>
              <h5>Property: <span className={styles.propertyName}>{propertyName}</span></h5>
            </div>
          )}

          {loading ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner}></div>
              <p>Loading upcoming viewings...</p>
            </div>
          ) : error ? (
            <div className={styles.errorState}>
              <div className={styles.errorIcon}>⚠️</div>
              <p className={styles.errorMessage}>{error}</p>
              <button 
                className={styles.retryButton}
                onClick={loadUpcomingViewings}
              >
                Try Again
              </button>
              
              {/* ✅ Display alternative viewings if available */}
              {alternativeViewings.length > 0 && (
                <div className={styles.alternativeViewings}>
                  <h6 className={styles.alternativeTitle}>Alternative Viewing Times:</h6>
                  <div className={styles.tableContainer}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Day</th>
                          <th>From</th>
                          <th>To</th>
                          <th>Staff Member</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {alternativeViewings.map((altViewing) => (
                          <tr key={altViewing.id}>
                            <td>{altViewing.day}</td>
                            <td>{formatTime(altViewing.time_from)}</td>
                            <td>{formatTime(altViewing.time_to)}</td>
                            <td>{altViewing.user?.name || "Staff Member"}</td>
                            <td>
                              <button
                                className={`${styles.actionButton} ${styles.attendButton}`}
                                onClick={() => handleConfirmAttendance(altViewing.id)}
                                disabled={actionLoading[altViewing.id]}
                              >
                                {actionLoading[altViewing.id] ? "⏳" : "Book This"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className={styles.viewingsTable}>
              {viewings.length > 0 ? (
                <div className={styles.tableContainer}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Day</th>
                        <th>From</th>
                        <th>To</th>
                        <th>Staff Member</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewings.map((viewing) => (
                        <tr key={viewing.id}>
                          <td className={styles.dayCell}>{viewing.day}</td>
                          <td className={styles.timeCell}>{formatTime(viewing.time_from)}</td>
                          <td className={styles.timeCell}>{formatTime(viewing.time_to)}</td>
                          <td className={styles.userCell}>{viewing.user?.name || "Staff Member"}</td>
                          <td className={styles.actionCell}>
                            {viewing.isConfirmed || confirmations.includes(viewing.id) ? (
                              <button
                                className={`${styles.actionButton} ${styles.cancelButton}`}
                                onClick={() => handleCancelAttendance(viewing.id)}
                                disabled={actionLoading[viewing.id]}
                              >
                                {actionLoading[viewing.id] ? (
                                  <span className={styles.buttonSpinner}>⏳</span>
                                ) : (
                                  "Cancel"
                                )}
                              </button>
                            ) : (
                              <button
                                className={`${styles.actionButton} ${styles.attendButton}`}
                                onClick={() => handleConfirmAttendance(viewing.id)}
                                disabled={actionLoading[viewing.id]}
                              >
                                {actionLoading[viewing.id] ? (
                                  <span className={styles.buttonSpinner}>⏳</span>
                                ) : (
                                  "I will attend"
                                )}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={styles.noViewings}>
                  <p>No upcoming viewings are currently available for this property.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className={styles.modalFooter}>
          <button
            type="button"
            className={styles.closeModalButton}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestViewingModal;